import type { RequestInfo } from '@warp-drive/core-types/request';
import { buildBaseURL } from '@warp-drive/utilities';

/**
 * GET /me materializes the current user and app-specific included relationships through the
 * authenticated store. Include paths may be comma-joined or passed as an array. Sparse fields
 * can omit expensive attributes from included resources when the caller does not use them.
 * Always reloads from the network so role grants and terms acceptance update session gates.
 * @param includes - Relationship paths to include in the response.
 * @param fields - Optional resource type to field-name lists for sparse fieldsets.
 * @returns A network-reloading request for the current user.
 */
export function meRequest(
  includes: string | readonly string[],
  fields: Readonly<Record<string, readonly string[]>> = {}
): RequestInfo {
  const base = buildBaseURL({
    identifier: { type: 'users' },
    op: 'query',
    resourcePath: 'me',
  });
  const include = Array.isArray(includes) ? includes.join(',') : includes;
  const fieldsets = Object.entries(fields)
    .map(([type, names]) => `&fields[${type}]=${names.join(',')}`)
    .join('');
  return {
    url: `${base}?include=${include}${fieldsets}`,
    method: 'GET',
    op: 'query',
    cacheOptions: { reload: true },
  };
}
